/* eslint-disable @typescript-eslint/no-explicit-any */
import { AnyFunction, IfNever } from 'types';

import { getLastItem } from './array';

interface Base {
  is: any;
  then: any;
}

type FunctionResult<T> = ReturnType<Extract<T, AnyFunction>>;

type ConditionResult<T extends Base> =
  | IfNever<FunctionResult<T['then']>, T['then'], FunctionResult<T['then']>>
  | Exclude<T['then'], AnyFunction>;

// View comment on types/general
// If this type is not on the file using it, VSCODE does not resolve it properly
type ExpandType<T> = T extends AnyFunction
  ? T
  : T extends infer O
    ? { [K in keyof O]: O[K] }
    : never;

export function cascadeEval<Cond extends Base>(
  conditions: Cond[],
): ExpandType<ConditionResult<Cond>> {
  const firstTrue = conditions.find(({ is }) => is);

  const matchCondition = firstTrue ? firstTrue.then : getLastItem(conditions)?.then;

  return (
    typeof matchCondition === 'function' ? (matchCondition as () => any)?.() : matchCondition
  ) as ConditionResult<Cond>;
}

interface QuickSwitchCondition<Ref, Result> {
  is: Ref extends string ? Ref | true : any;
  then: Result | (() => Result);
}

export function quickSwitch<RefValue, Result>(
  ref: RefValue,
  conditions: QuickSwitchCondition<RefValue, Result>[],
): Result {
  const firstTrue = conditions.find(({ is }) => ref === is);

  const then = firstTrue ? firstTrue.then : getLastItem(conditions)?.then;

  return (typeof then === 'function' ? (then as () => Result)?.() : then) as Result;
}
